// ---------------------------------------------------------
// Datei: imageService.ts
// Zweck: Zentrale Foto-Normalisierung fuer Uploads
// Kontext: Mobile Uploads koennen je nach Quelle HEIC/PNG/WebP liefern.
// Stand: 24. Juli 2026
// ---------------------------------------------------------

import { v4 as uuidv4 } from 'uuid';
import loadImage from 'blueimp-load-image';

export const PHOTO_JPEG_MIME_TYPE = 'image/jpeg';
export const PHOTO_JPEG_EXTENSION = '.jpg';

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_TARGET_BYTES = 1024 * 1024;
const DEFAULT_QUALITIES = [0.86, 0.8, 0.72, 0.64];

// Auch Anhänge ohne zuverlässigen MIME-Type werden anhand dieser Endungen als Fotos erkannt.
export const PHOTO_EXTENSIONS = /\.(jpe?g|png|webp|heic|heif|bmp|gif|tiff?)$/i;

export interface PhotoNormalizationOptions {
  fileName?: string;
  maxDimension?: number;
  targetBytes?: number;
  qualities?: number[];
}

export interface NormalizedPhoto {
  dataUrl: string;
  file: File;
  fileName: string;
  contentType: typeof PHOTO_JPEG_MIME_TYPE;
  size: number;
}

const normalizeJpegFileName = (fileName?: string): string => {
  const rawName = fileName?.trim() || generateUniqueImageName();
  const baseName = rawName.replace(/\.[^.]+$/, '') || 'foto';
  return `${baseName}${PHOTO_JPEG_EXTENSION}`;
};

const getDataUrlMimeType = (dataUrl: string): string | null =>
  dataUrl.match(/^data:([^;,]+)[;,]/i)?.[1]?.toLowerCase() || null;

const canvasToJpegBlob = (
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }
        reject(new Error('Bild konnte nicht als JPG exportiert werden'));
      },
      PHOTO_JPEG_MIME_TYPE,
      quality
    );
  });

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Bild konnte nicht gelesen werden'));
    reader.readAsDataURL(blob);
  });

export const isPhotoDataUrl = (dataUrl: string): boolean => {
  const mimeType = getDataUrlMimeType(dataUrl);
  return !!mimeType && mimeType.startsWith('image/') && mimeType !== 'image/svg+xml';
};

export const isPhotoFile = (file: File): boolean => {
  const type = file.type.toLowerCase();
  return (type.startsWith('image/') && type !== 'image/svg+xml') || PHOTO_EXTENSIONS.test(file.name);
};

export const isJpegPhotoDataUrl = (dataUrl: string): boolean =>
  getDataUrlMimeType(dataUrl) === PHOTO_JPEG_MIME_TYPE;

export const normalizePhotoDataUrl = async (
  dataUrl: string,
  options: PhotoNormalizationOptions = {}
): Promise<NormalizedPhoto> => {
  if (!isPhotoDataUrl(dataUrl)) {
    throw new Error('Nur Foto-Dateien koennen als JPG normalisiert werden');
  }

  const fileName = normalizeJpegFileName(options.fileName);
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const targetBytes = options.targetBytes ?? DEFAULT_TARGET_BYTES;
  const qualities = options.qualities?.length ? options.qualities : DEFAULT_QUALITIES;

  try {
    const loadImageResult = await loadImage(dataUrl, {
      canvas: true,
      orientation: true,
      maxWidth: maxDimension,
      maxHeight: maxDimension,
    });

    if (!loadImageResult.image || !(loadImageResult.image instanceof HTMLCanvasElement)) {
      throw new Error('Bild konnte nicht verarbeitet werden');
    }

    const canvas = loadImageResult.image;
    let selectedBlob: Blob | null = null;

    for (const quality of qualities) {
      const blob = await canvasToJpegBlob(canvas, quality);
      selectedBlob = blob;
      if (blob.size <= targetBytes) {
        break;
      }
    }

    if (!selectedBlob) {
      throw new Error('Bild konnte nicht als JPG exportiert werden');
    }

    const normalizedDataUrl = await blobToDataUrl(selectedBlob);
    const file = new File([selectedBlob], fileName, {
      type: PHOTO_JPEG_MIME_TYPE,
      lastModified: Date.now(),
    });

    return {
      dataUrl: normalizedDataUrl,
      file,
      fileName,
      contentType: PHOTO_JPEG_MIME_TYPE,
      size: selectedBlob.size,
    };
  } catch (error) {
    console.error('Fehler bei der Bildverarbeitung:', error);
    throw new Error('Foto konnte nicht in JPG umgewandelt werden');
  }
};

export const normalizePhotoFile = async (
  file: File,
  options: PhotoNormalizationOptions = {}
): Promise<NormalizedPhoto> => {
  if (!isPhotoFile(file)) {
    throw new Error('Nur Foto-Dateien koennen als JPG normalisiert werden');
  }

  const dataUrl = await blobToDataUrl(file);
  return normalizePhotoDataUrl(dataUrl, {
    ...options,
    fileName: options.fileName ?? file.name,
  });
};

/**
 * Kompatibilitaets-Wrapper fuer alte Aufrufer.
 * @param dataUrl Das Bild als Data-URL
 * @returns EXIF-korrigiertes Bild als JPG Data-URL
 */
export const compressImage = async (dataUrl: string): Promise<string> => {
  const normalized = await normalizePhotoDataUrl(dataUrl);
  return normalized.dataUrl;
};

/**
 * Hilfsfunktion zum Generieren eines eindeutigen Dateinamens
 */
export const generateUniqueImageName = (): string => {
  return `image-${uuidv4()}.jpg`;
};

/**
 * Dateiname für Fotos mit bekannter Seriennummer (z. B. Seriennummer-Außeneinheit).
 * Seriennummer wird auf dateisystemsichere Zeichen reduziert; ohne gültigen Rest
 * wird auf den UUID-Namen zurückgefallen.
 */
export const generateSerialNumberImageName = (serialNumber: string): string => {
  const sanitized = serialNumber.trim().replace(/[^A-Za-z0-9-]+/g, '');
  if (!sanitized) {
    return generateUniqueImageName();
  }
  return `image-serial-no-${sanitized}.jpg`;
};

/**
 * Dateiname für Fotos generischer Checklisten-Dokumente (z.B. "ausseneinheit-fundament",
 * "aussenfuehler") — sprechender Name statt reiner UUID (Nutzer-Rückmeldung), damit Dateien auch
 * direkt in Drive dem Dokumenttyp zuordenbar sind. Kurzer UUID-Suffix bleibt nötig, weil
 * Multi-Upload-Dokumente mehrere Fotos desselben Typs erlauben und sonst kollidieren würden.
 */
export const generateDocumentImageName = (documentId: string): string => {
  const sanitized = documentId
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!sanitized) {
    return generateUniqueImageName();
  }
  return `image-${sanitized}-${uuidv4().slice(0, 8)}.jpg`;
};
